/**
 * <scroll-indicator for="…"> — the horizontal indicator under a row that scrolls sideways on a
 * phone or tablet (SAVETTE-70): the visual category row, the product page's "You may also like",
 * and a listing card's images (laid over the image there). It is drawn as the product page's vertical gallery indicator is (sections/product.liquid,
 * from 1025): a 1px track 112 long with a 32px segment that travels it, here sideways as the row
 * scrolls. Its markup and styles come from snippets/scroll-indicator.liquid.
 *
 * The row it reports on is the element whose id its `for` attribute names, as a label's `for`
 * names its control, so the row needs nothing of its own. The row is looked up when the
 * indicator connects, so the indicator goes after the row in the markup; a row filled in after
 * load (the recommendations arrive through innerHTML) brings its indicator in with it, and the
 * indicator connects once both are in the page. A row whose content changes while its box does
 * not (a card switching colour, assets/product-card.js) sends itself a scroll event to be measured
 * again.
 *
 * It shows only while the row is wider than its box, so a row that fits shows nothing, and from
 * 1025 the snippet's CSS hides it. It is decorative (aria-hidden): the row is the control. Nothing
 * animates beyond the segment's short transition, which the CSS drops under
 * prefers-reduced-motion.
 *
 * An indicator with a `media` attribute (a media query) watches its row only while the query
 * matches: a listing renders one per card, and on a desktop, where the CSS hides them all, they
 * then observe and measure nothing. It starts again when the screen comes back into the query.
 * Without the attribute it always watches, as the row indicators always have.
 */
class ScrollIndicator extends HTMLElement {
  connectedCallback() {
    this.thumb = this.querySelector('[data-indicator-thumb]');
    const id = this.getAttribute('for');
    this.scroller = id ? document.getElementById(id) : null;
    if (!this.thumb || !this.scroller) return;

    const query = this.getAttribute('media');
    this.mediaQuery = query ? window.matchMedia(query) : null;
    this.mediaQuery?.addEventListener('change', this.onMediaChange);
    this.onMediaChange();
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
    this.mediaQuery = null;
    this.stop();
  }

  onMediaChange = () => {
    if (!this.mediaQuery || this.mediaQuery.matches) this.start();
    else this.stop();
  };

  start() {
    if (this.watching) return;
    this.watching = true;
    this.scroller.addEventListener('scroll', this.schedule, { passive: true });
    // the row's box changes with the screen; observing it also runs the first measure
    this.resizeObserver = new ResizeObserver(this.schedule);
    this.resizeObserver.observe(this.scroller);
  }

  stop() {
    if (!this.watching) return;
    this.watching = false;
    this.scroller?.removeEventListener('scroll', this.schedule);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  /** One measure per frame, however many scroll events arrive in it. */
  schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  };

  /** Everything is read before anything is written, so a frame never forces a second layout. */
  update() {
    const travel = this.scroller.scrollWidth - this.scroller.clientWidth;
    const at = Math.abs(this.scroller.scrollLeft);
    const wasHidden = this.hidden;
    // a hidden indicator has no width to read; it is measured the frame after it shows
    const room = wasHidden ? 0 : Math.max(this.clientWidth - this.thumb.offsetWidth, 0);

    // a row that fits has nothing to report
    this.hidden = travel < 1;
    if (this.hidden) return;
    if (wasHidden) {
      this.schedule();
      return;
    }
    const progress = Math.min(Math.max(at / travel, 0), 1);
    this.thumb.style.transform = `translateX(${(room * progress).toFixed(2)}px)`;
  }
}

if (!customElements.get('scroll-indicator')) {
  customElements.define('scroll-indicator', ScrollIndicator);
}
